import html2canvas from "html2canvas";
import { dataURItoBlob } from "./blobConvertion";
import getElement from "./getElement";

/**
 * @typedef SaveScreenshotOptions
 * @property {boolean | undefined} includeVideos paint <video> frames into the clone (html2canvas cannot capture them)
 * @property {boolean | undefined} excludeText hide subtitle overlays and text-only workspace items
 * @property {number | undefined} videoReadyTimeout ms to wait for videos to finish seeking
 */

const waitForVideosReady = (root, timeout = 3000) => new Promise((resolve) => {
    const startedAt = Date.now();

    const poll = () => {
        const videos = [...root.querySelectorAll("video")].filter((video) => {
            const rect = video.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });

        if (videos.length === 0 || Date.now() - startedAt >= timeout) {
            resolve();
            return;
        }

        const allReady = videos.every((video) => video.readyState >= 2 && !video.seeking);
        if (allReady) {
            resolve();
            return;
        }

        requestAnimationFrame(poll);
    };

    poll();
});

const hideTextOverlays = (clonedRoot) => {
    const sceneContainer = clonedRoot.querySelector("#scene-container");
    if (!sceneContainer) return;

    sceneContainer.querySelectorAll(":scope > div").forEach((el) => {
        if (!el.classList.contains("scene-item")) {
            el.style.setProperty("display", "none", "important");
        }
    });

    sceneContainer.querySelectorAll(".scene-item").forEach((item) => {
        if (!item.querySelector("video, img, canvas")) {
            item.style.setProperty("display", "none", "important");
        }
    });
};

const paintVideosInClone = (originalRoot, clonedRoot, clonedDoc) => {
    const originalVideos = [...originalRoot.querySelectorAll("video")];
    const clonedVideos = [...clonedRoot.querySelectorAll("video")];

    clonedVideos.forEach((clonedVideo, index) => {
        const originalVideo = originalVideos[index];
        if (!originalVideo || originalVideo.readyState < 2) return;

        const width = clonedVideo.offsetWidth || clonedVideo.clientWidth;
        const height = clonedVideo.offsetHeight || clonedVideo.clientHeight;
        if (width <= 0 || height <= 0) return;

        const canvas = clonedDoc.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.className = clonedVideo.className;
        canvas.style.cssText = clonedVideo.style.cssText;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        try {
            ctx.drawImage(originalVideo, 0, 0, width, height);
        } catch (err) {
            return;
        }

        clonedVideo.parentNode.replaceChild(canvas, clonedVideo);
    });
};

export function saveScreenshot(elem, width = 600, options = {}) {
    const {
        includeVideos = false,
        excludeText = false,
        videoReadyTimeout = 3000,
    } = options;

    return new Promise((resolve, reject) => {
        try {
            let element = elem;
            if (typeof elem === "string") {
                element = getElement(elem);
            }

            if (!element) {
                resolve(null);
                return;
            }

            const capture = () => {
                const bounds = element.getBoundingClientRect();
                html2canvas(element, {
                    useCORS: true,
                    logging: false,
                    scale: width / bounds.width,
                    onclone: (clonedDoc, clonedElement) => {
                        if (excludeText) {
                            hideTextOverlays(clonedElement);
                        }
                        if (includeVideos) {
                            paintVideosInClone(element, clonedElement, clonedDoc);
                        }
                    },
                })
                    .then((canvas) => {
                        const projectUrl = canvas.toDataURL();
                        resolve(dataURItoBlob(projectUrl), projectUrl);
                    })
                    .catch(reject);
            };

            if (includeVideos) {
                waitForVideosReady(element, videoReadyTimeout).then(capture);
            } else {
                capture();
            }
        } catch (err) {
            reject(err);
        }
    });
}

export const convertBlobToBase64URL = (blob) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        resolve(reader.readAsDataURL(blob));
    });
}
